import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { SkeletonSelectTrigger } from "@/components/ui/skeleton";
import {
  platforms_shopify,
  subscription_plans_route,
  users_subscription_route,
} from "@/constants";
import { useCreateShopifyBilling } from "@/hooks/shopify-billing";
import { stripePortalQuery, useCreateStripeCheckout } from "@/hooks/stripe";
import { authedFetch } from "@/lib/api-client";
import type { Plan, ShopifySession, Subscription } from "@/lib/types";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Await, createFileRoute } from "@tanstack/react-router";
import { InfinityIcon, X } from "lucide-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod/v4";

async function getSubscriptions(token: string, orgId: string) {
  const resp = await authedFetch(users_subscription_route, {
    method: "GET",
    token,
    orgId,
  });

  if (resp.status > 399) {
    throw new Error("error fetching subscription");
  }

  return resp;
}

async function getPlans(token: string, orgId: string) {
  const resp = await authedFetch(subscription_plans_route, {
    method: "GET",
    token,
    orgId,
  });

  if (resp.status > 399) {
    throw new Error("error fetching subscription plans");
  }

  return (await resp.json()) as Plan[];
}

async function getShopifySessions(token: string, orgId: string) {
  const resp = await authedFetch(platforms_shopify, {
    method: "GET",
    token,
    orgId,
  });

  if (resp.status > 399) {
    throw new Error("error fetching sessions");
  }

  return (await resp.json()) as ShopifySession[];
}

export const Route = createFileRoute("/$org/settings/billing")({
  component: BillingComponent,
  loader: async ({ context }) => {
    const { getToken } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    try {
      const resp = await getSubscriptions(getToken() as string, orgId);

      const token = resp.headers.get("access-token");
      if (token) {
        context.auth.setToken(token);
      }

      const subscription = (await resp.json()) as Subscription;

      const plans = getPlans(getToken() as string, orgId);

      const shopifySessions = getShopifySessions(getToken() as string, orgId);

      return { subscription, plans, shopifySessions };
    } catch (e) {
      console.log(e);
      return {
        subscription: null,
        plans: [],
        shopifySessions: [],
      };
    }
  },
});

const formSchema = z.object({
  shopify_sub: z.literal(["true", "false"]),
  subscription: z.string(),
  shopify_session: z.string().optional(),
  provider: z.literal(["SHOPIFY", "STRIPE"]),
});

type formSchemaType = z.infer<typeof formSchema>;

function BillingComponent() {
  const { t, i18n } = useTranslation();
  const { subscription, plans, shopifySessions } = Route.useLoaderData();
  const [showDownloadAlert, setShowDownloadAlert] = useState(false);
  const mutation = useCreateShopifyBilling();
  const stripeMutation = useCreateStripeCheckout();

  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shopify_sub: subscription?.shopify_sub ? "true" : "false",
      subscription: subscription?.plan_id,
      shopify_session: undefined,
      provider: subscription?.provider ?? "STRIPE",
    },
  });

  const handleClick = () => {
    setShowDownloadAlert(false);
  };

  const onSubmit: SubmitHandler<formSchemaType> = (data) => {
    if (data.provider === "SHOPIFY") {
      mutation.mutate({
        id: data.subscription,
        shopify_session_id: data.shopify_session,
      });
    } else {
      stripeMutation.mutate({
        plan_id: data.subscription,
        provider: data.provider,
      });
    }
  };

  return (
    <div>
      <div className="mx-auto max-w-3xl space-y-4">
        {subscription && subscription.trial_end && (
          <Alert variant={"caution"} className="">
            <AlertDescription className="w-full text-center flex flex-row">
              <Trans
                i18nKey="settings.billing.trialEnds"
                values={{
                  date: new Date(subscription.trial_end).toLocaleDateString(
                    i18n.language,
                  ),
                }}
                components={{ bold: <span className="font-semibold" /> }}
              />
            </AlertDescription>
          </Alert>
        )}

        {showDownloadAlert && (
          <Alert className="flex">
            <AlertDescription>
              <span>
                {t("settings.billing.installShopifyApp")}
              </span>
              <a
                className="underline hover:text-gray-200"
                href={"https://apps.shopify.com/ebay-sync"}
              >
                Inventoree
              </a>
            </AlertDescription>
            <X className="cursor-pointer relative" onClick={handleClick} />
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t("settings.billing.plan")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex space-x-2">
                <span className="text-muted-foreground">
                  {t("settings.billing.currentPlan")}
                </span>
                <span>{subscription?.plan_name ?? "-"}</span>
              </div>

              {subscription?.stripe_active_sub ? (
                <StripeCustomerPortal />
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex flex-row space-x-2"
                  >
                    <FormField
                      control={form.control}
                      name="provider"
                      render={({ field }) => <Input type="hidden" {...field} />}
                    />
                    <FormField
                      name="shopify_sub"
                      control={form.control}
                      render={({ field }) => <Input type="hidden" {...field} />}
                    />

                    <FormField
                      name="subscription"
                      control={form.control}
                      render={({ field }) => (
                        <Await
                          promise={plans as Promise<Plan[]>}
                          fallback={
                            <SkeletonSelectTrigger className="h-9 w-[200px]" />
                          }
                        >
                          {(data) => (
                            <>
                              <Select onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger className="w-[200px]">
                                    <SelectValue
                                      placeholder={t(
                                        "settings.billing.updateSubscription",
                                      )}
                                    />
                                  </SelectTrigger>
                                </FormControl>

                                <SelectContent>
                                  {data.map((plan, idx) => (
                                    <SelectItem key={idx} value={plan.id}>
                                      {plan.display_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </>
                          )}
                        </Await>
                      )}
                    />

                    <Await
                      promise={shopifySessions as Promise<ShopifySession[]>}
                      fallback={
                        <SkeletonSelectTrigger className="h-9 w-[200px]" />
                      }
                    >
                      {(data) => (
                        <>
                          {!subscription?.shopify_sub && data.length > 0 && (
                            <FormField
                              name="shopify_session"
                              control={form.control}
                              render={({ field }) => (
                                <Select onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger className="w-[300px]">
                                      <SelectValue
                                        placeholder={t(
                                          "settings.billing.chooseShopifyAccount",
                                        )}
                                      />
                                    </SelectTrigger>
                                  </FormControl>

                                  <SelectContent>
                                    {data.map((acc, idx) => (
                                      <SelectItem key={idx} value={acc.id}>
                                        {acc.alias}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          )}
                        </>
                      )}
                    </Await>

                    <Button type="submit">
                      {t("settings.billing.choosePlan")}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("settings.billing.usage")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="border rounded-sm flex flex-col">
              <div className="flex flex-row">
                <div className="p-2 w-1/2 flex flex-col space-y-2">
                  <span className="text-muted-foreground">
                    {t("settings.billing.orders")}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xl font-medium">
                      {subscription?.order_usage}
                    </span>
                    <span className="text-muted-foreground">/</span>
                    <span className="text-xl font-medium">
                      {subscription?.order_limit! > 0 ? (
                        subscription?.order_limit
                      ) : (
                        <InfinityIcon />
                      )}
                    </span>
                  </div>
                </div>
                <div className="p-2 w-1/2 border-l flex flex-col space-y-2">
                  <span className="text-muted-foreground">
                    {t("settings.billing.usageCost")}
                  </span>
                  <div className="flex items-center space-x-0.5">
                    <span className="text-muted-foreground">$</span>
                    <span className="text-xl font-medium">
                      {subscription?.cost.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="p-2">
                <p className="text-sm">
                  {t("settings.billing.changePlanHint")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StripeCustomerPortal() {
  const { t } = useTranslation();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { data: portalSession } = useQuery(
    stripePortalQuery({
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );

  return (
    <a
      href={portalSession?.url}
      className={buttonVariants({ variant: "default" })}
    >
      {t("settings.billing.manageBilling")}
    </a>
  );
}
